<template>
  <v-dialog
      :model-value="isVisible"
      max-width="1024"
      @update:model-value="dismiss"
  >
    <v-card
        prepend-icon="mdi-check"
        title="Invoice creation"
    >
      <v-card-text
          class="mb-2"
      >
        <v-form
            ref="invoiceCreation"
        >
          <v-row>
            <v-col
                cols="12"
                md="6"
            >
              <v-text-field
                  v-model="invoice.fullname.value"
                  :rules="invoice.fullname.rules"
                  :disabled="isLoading"
                  :loading="isLoading"
                  label="Fullname"
              />
            </v-col>
            <v-col
                cols="12"
                md="6"
            >
              <v-text-field
                  v-model="invoice.address.value"
                  :rules="invoice.address.rules"
                  :disabled="isLoading"
                  :loading="isLoading"
                  label="Address"
              />
            </v-col>
            <v-col
                cols="12"
            >
              <v-text-field
                  v-model="invoice.manufacturer.value"
                  :disabled="isLoading"
                  label="Manufacturer"
              />
            </v-col>
            <v-col
                cols="12"
                md="6"
            >
              <v-select
                  :items="bankOptions"
                  v-model="invoice.bank.value"
                  :rules="invoice.bank.rules"
                  :disabled="isLoading"
                  :loading="isLoading"
                  label="Bank"
              />
            </v-col>
            <v-col
                cols="12"
                md="6"
            >
              <v-select
                  :items="currencyOptions"
                  v-model="invoice.currency.value"
                  :rules="invoice.currency.rules"
                  :disabled="isLoading"
                  :loading="isLoading"
                  label="Currency"
              />
            </v-col>
            <v-col
                cols="12"
                md="6"
            >
              <v-select
                  :items="termOptions"
                  v-model="invoice.terms.value"
                  :rules="invoice.terms.rules"
                  :disabled="isLoading"
                  :loading="isLoading"
                  label="Terms"
              />
            </v-col>
            <v-col
                cols="12"
                md="6"
            >
              <v-text-field
                  v-model="invoice.destination.value"
                  :rules="invoice.destination.rules"
                  :disabled="isLoading"
                  :loading="isLoading"
                  label="Destination"
              />
            </v-col>
            <v-col
                cols="12"
            >
              <v-form
                  ref="itemCreation"
              >
              <v-table>
                <thead>
                <tr>
                  <th class="text-center">
                    Description
                  </th>
                  <th class="text-center">
                    Model
                  </th>
                  <th class="text-center">
                    Quantity
                  </th>
                  <th class="text-center">
                    Price
                  </th>
                  <th class="text-center">
                    Total price
                  </th>
                  <th class="text-center">
                  </th>
                </tr>
                </thead>
                <tbody>
                  <invoice-product-table-row
                    v-for="(item, index) in invoice.items.value"
                    :key="index"
                    :index="index"
                    :is-disabled="isLoading"
                    v-model:init-is-editing="item.isEditing"
                    :init-description="item.description"
                    :init-model="item.model"
                    :init-quantity="item.quantity"
                    :init-price="item.price"
                    :currency="invoice.currency.value"
                    :get-item-validation-state="getItemValidationState"
                    @remove-invoice-item="removeItem"
                    @save-invoice-item="saveItem"
                  />
                <tr>
                  <td class="text-center" colspan="2">Total</td>
                  <td class="text-center" colspan="1">{{ invoiceTotalQuantity }}</td>
                  <td class="text-center" colspan="2">{{ invoiceTotalPrice }}</td>
                  <td class="text-center">
                    <v-btn
                        :disabled="isLoading"
                        size="x-small"
                        icon="mdi-plus"
                        color="success"
                        @click="addItem"
                    />
                  </td>
                </tr>
                </tbody>
              </v-table>
              </v-form>
            </v-col>
            <v-col
                v-show="error.isVisible"
                cols="12"
            >
              <small class="text-caption text-red">{{ error.message }}</small>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer/>
        <v-btn
            text="Close"
            variant="plain"
            @click="dismiss"
        />
        <v-btn
            color="primary"
            text="Create"
            variant="tonal"
            @click="createInvoice"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {InvoiceTerm} from "@/common/enum/invoice_terms";
import InvoiceProductTableRow from "@/components/Invoice/InvoiceProductTableRow.vue";
import {Bank} from "@/common/enum/bank";
import {Currency} from "@/common/enum/currency";
import {numbers} from "@/common/numbers";

export default {
  name: "InvoiceCreationDialog",
  components: {InvoiceProductTableRow},
  emits: ['dismiss', 'createInvoice'],
  props: {
    isLoading: Boolean,
    isVisible: Boolean,
    error: {
      isVisible: Boolean,
      message: String
    }
  },
  watch: {
    isLoading() {
      if (!this.isLoading && !this.error.isVisible) {
        this.emptyingInvoice()
      }
    }
  },
  computed: {
    bankOptions() {
      return Object.entries(Bank).reduce((state, [key, value]) => {
        state.push({'value': key, 'title': value})
        return state
      }, [])
    },
    currencyOptions() {
      return Object.entries(Currency).reduce((state, [key, value]) => {
        state.push({'value': key, 'title': value})
        return state
      }, [])
    },
    termOptions() {
      return Object.entries(InvoiceTerm).reduce((state, [key, value]) => {
        state.push({'value': key, 'title': value})
        return state
      }, [])
    },
    invoiceTotalQuantity() {
      return this.invoice.items.value.reduce((sum, item) => sum + Number(item.quantity), 0)
    },
    invoiceTotalPrice() {
      return numbers.formatMoney(this.invoice.items.value.reduce((sum, item) => sum + item.price * item.quantity, 0), this.invoice.currency.value)
    }

  },
  data() {
    return {
      invoice: {
        fullname: {
          value: null,
          rules: [
            v => !!v || 'Fullname is required',
          ]
        },
        address: {
          value: null,
          rules: [
            v => !!v || 'Address is required',
          ]
        },
        bank: {
          value: null,
          rules:[
            v => !!v || 'Bank is required',
          ]
        },
        manufacturer: {
          value: null,
        },
        terms: {
          value: null,
          rules: [
            v => !!v || 'Terms is required',
          ]
        },
        destination: {
          value: null,
          rules: [
            v => !!v || this.terms !== InvoiceTerm['C&F'] || 'Destination is required for C&F'
          ]
        },
        currency: {
          value: Currency.USD,
          rules: [
            v => !!v || 'Currency is required',
          ]
        },
        items: {
          value: []
        }
      },
    }
  },
  methods: {
    addItem() {
      this.invoice.items.value.push({
          isEditing: true,
          description: null,
          model: null,
          quantity:null,
          price: null,
        })
    },
    removeItem(removableIndex) {
      this.invoice.items.value = this.invoice.items.value.filter(function (value, index) {
        return index !== removableIndex
      })
    },
    async getItemValidationState() {
      return (await this.$refs.itemCreation.validate()).valid
    },
    async saveItem(updatedIndex, updatedItem) {
      if (!(await this.$refs.itemCreation.validate()).valid) {
        return
      }

      this.invoice.items.value.forEach(function (value, index) {
        if (index === updatedIndex) {
          value.isEditing = false
          value.description = updatedItem.description.value
          value.model = updatedItem.model.value
          value.quantity = updatedItem.quantity.value
          value.price = updatedItem.price.value
        }
      })
    },
    dismiss() {
      this.emptyingInvoice()
      this.$emit('dismiss')
    },
    emptyingInvoice() {
      this.invoice.fullname.value = null
      this.invoice.address.value = null
      this.invoice.bank.value = null
      this.invoice.manufacturer.value = null
      this.invoice.terms.value = null
      this.invoice.destination.value = null
      this.invoice.currency.value = Currency.USD
      this.invoice.items.value = []
    },
    async createInvoice() {
      if (!(await this.$refs.itemCreation.validate()).valid) {
        return
      }

      if (!(await this.$refs.invoiceCreation.validate()).valid) {
        return
      }

      this.invoice.items.value.forEach(function (value) {
        value.isEditing = false
      })
      this.invoice.items.value = this.invoice.items.value.filter((item) => item.description !== null)

      await this.$emit('createInvoice', this.invoice)
    }
  }
}
</script>

<style scoped>

</style>